import React, { useContext } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'
import { SearchIcon } from 'lucide-react'
import { FieldContext } from './Field'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string
  asChild?: boolean
  invalid?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  status?: 'success' | 'error'
  large?: boolean
  id?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      asChild,
      status: _status,
      children,
      leadingIcon,
      trailingIcon,
      disabled: _disabled,
      large: _large,
      id: _id,
      ...props
    },
    ref,
  ) => {
    const context = useContext(FieldContext)
    const disabled = _disabled ?? context?.disabled
    const status = disabled ? 'disabled' : _status ?? context?.status
    const id = _id ?? context?.fieldId
    const large = _large ?? context?.large

    const Comp = asChild ? Slot : 'input'

    return (
      <div
        data-slot="control"
        data-status={status}
        data-disabled={disabled ? true : undefined}
        className="group relative flex w-full items-center rounded-md bg-background px-2 text-sm text-txt-primary caret-accent shadow-input-default transition-all  duration-300 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-txt-placeholder focus-within:!shadow-input-focus hover:shadow-input-hover active:shadow-input-focus disabled:cursor-not-allowed data-[status=disabled]:pointer-events-none data-[status=disabled]:bg-gray-tr-08 data-[status=disabled]:text-txt-disabled data-[status=error]:shadow-input-invalid data-[status=success]:shadow-input-success data-[status=error]:focus-within:!shadow-input-invalid data-[status=success]:focus-within:!shadow-input-success data-[status=error]:hover:shadow-input-invalid data-[status=success]:hover:shadow-input-success"
      >
        {leadingIcon && (
          <div className="flex items-center text-txt-secondary group-data-[disabled]:text-txt-disabled">
            <Slot className={cn('size-6 ', leadingIcon?.props?.className)}>
              {leadingIcon}
            </Slot>
          </div>
        )}
        <Comp
          id={id}
          className={cn(
            'flex h-10 w-full px-2 focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-transparent data-[large]:h-12',
            className,
          )}
          ref={ref}
          data-invalid={status === 'error' ? true : undefined}
          data-leading-icon={leadingIcon ? true : undefined}
          data-trailing-icon={trailingIcon ? true : undefined}
          disabled={disabled}
          data-large={large ? true : undefined}
          {...props}
        />
        {trailingIcon && (
          <div className="group-hover:text-green-500-- flex items-center text-txt-secondary group-data-[disabled]:text-txt-disabled">
            <Slot className={cn('size-6 ', trailingIcon?.props?.className)}>
              {trailingIcon}
            </Slot>
          </div>
        )}
      </div>
    )
  },
)

export { Input }
